import {
	type ApiErrorResponse,
	type PostRegistrationUserValidationEmailPayload,
	type PostUserRequirementEmailGenerateCodeData,
	type PostUserRequirementEmailGenerateCodePayload,
	type PutUserUserRequirementStateMeEmailRequirementData,
	type UserEmailRequirementUserProvidedValues
} from '@capsicum/common';
import { getResponseStatusIfAxiosError, registrationApi, requirementApi, userApi } from '@capsicum/common';
import { useMutation } from '@tanstack/react-query';

import { useUser } from '../../../auth/hooks/useUser';
import { RequirementCompletionEventsEnum, useTrackRequirementsEvents } from '../../hooks/useTrackRequirementsEvents';

import type { AxiosError } from 'axios';

const UseEmailAvailabilityCheckQueryKey = ['email-availability-check'];

export const useEmailAvailabilityCheck = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation({
		mutationFn: async (email: PostRegistrationUserValidationEmailPayload) =>
			(await registrationApi).postRegistrationUserValidationEmail(email),
		mutationKey: UseEmailAvailabilityCheckQueryKey
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutateAsync
	};
};

const UseSendCodeVerificationToEmailQueryKey = ['send-code-verification-to-email'];

export const useSendCodeVerificationToEmail = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation<
		PostUserRequirementEmailGenerateCodeData,
		AxiosError<ApiErrorResponse>,
		PostUserRequirementEmailGenerateCodePayload
	>({
		mutationFn: async ({ email }: PostUserRequirementEmailGenerateCodePayload) =>
			(await requirementApi).postUserRequirementEmailGenerateCode(
				{ email },
				{
					customErrorInterceptor: e => getResponseStatusIfAxiosError(e) === 429
				}
			),
		mutationKey: UseSendCodeVerificationToEmailQueryKey
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutateAsync
	};
};

const UseSubmitEmailRequirementVerificationCodeQueryKey = ['submit-email-verification-code'];

export const useSubmitEmailRequirementVerificationCode = () => {
	const { refetchUser } = useUser();
	const { trackRequirementsCompletion } = useTrackRequirementsEvents();
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation<
		PutUserUserRequirementStateMeEmailRequirementData,
		AxiosError<ApiErrorResponse>,
		UserEmailRequirementUserProvidedValues
	>({
		mutationFn: async ({ email, generatedCode }: UserEmailRequirementUserProvidedValues) =>
			(await userApi).putUserUserRequirementStateMeEmailRequirement(
				{ email, generatedCode },
				{
					customErrorInterceptor: e => getResponseStatusIfAxiosError(e) === 400
				}
			),
		mutationKey: UseSubmitEmailRequirementVerificationCodeQueryKey,
		onError: () => trackRequirementsCompletion(RequirementCompletionEventsEnum.failed, 'email_verification'),
		onSuccess: () => {
			refetchUser();
			trackRequirementsCompletion(RequirementCompletionEventsEnum.done, 'email_verification');
		}
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutateAsync
	};
};
